// ---- FW-2A: item "Sales" section (catalogue defaults), mounted only when the item is sellable ----
import React, { useEffect, useState } from 'react';
import { Box, TextField } from '@mui/material';
import { AccountService } from '../../services/account.service';
import { Account } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { leafAccounts } from '../../utils/postingAccounts';
import TaxCodeSelect from '../tax/TaxCodeSelect';
import { hasTaxCodes, useTaxCodeLookup } from '../tax/useTaxCodeLookup';
import { AuthService } from '../../services/auth.service';

export interface ItemSalesValue {
  salePrice?: number | null;
  defaultRevenueAccountId?: string | null;
  defaultTaxCodeId?: string | null;
}

interface Props {
  value: ItemSalesValue;
  onChange: (patch: ItemSalesValue) => void;
}

const ItemSalesFields: React.FC<Props> = ({ value, onChange }) => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const taxLookup = useTaxCodeLookup(AuthService.getSelectedInstitution());

  useEffect(() => {
    let live = true;
    AccountService.getAccountsByType('REVENUE')
      .then(a => { if (live) setAccounts(Array.isArray(a) ? a : []); })
      .catch(() => { if (live) setAccounts([]); });
    return () => { live = false; };
  }, []);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }} data-help-section="store.itemForm.sales" data-testid="item-sales-fields">
      <TextField
        data-help="store.itemForm.sales.price" label="Sale price" type="number" size="small"
        sx={{ flex: '1 1 140px', maxWidth: 220 }}
        value={value.salePrice ?? ''}
        onChange={e => onChange({ salePrice: e.target.value === '' ? null : Number(e.target.value) })}
        inputProps={{ min: 0, step: 'any' }}
        data-testid="sale-price-input"
      />
      <Box sx={{ flex: '1 1 220px', maxWidth: 320 }}>
        <AccountAutocomplete
          accounts={leafAccounts(accounts)}
          value={value.defaultRevenueAccountId ?? ''}
          data-help="store.itemForm.sales.revenue" label="Default revenue account"
          onChange={id => onChange({ defaultRevenueAccountId: id || null })}
        />
      </Box>
      {hasTaxCodes(taxLookup) && (
        <Box sx={{ flex: '1 1 220px', maxWidth: 320 }} data-help="store.itemForm.sales.taxCode">
          <TaxCodeSelect
            lookup={taxLookup}
            side="OUTPUT"
            value={value.defaultTaxCodeId}
            onChange={cid => onChange({ defaultTaxCodeId: cid })}
          />
        </Box>
      )}
    </Box>
  );
};

export default ItemSalesFields;
// ---- end FW-2A ----
